import {
  Button,
  Group,
  Input,
  Loader,
  Paper,
  Stack,
  Text,
  TextInput
} from '@mantine/core'
import { IconRefresh, IconSearch } from '@tabler/icons-react'
import { ReactNode, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { useListMilestoneServerCameras } from '@/queries/vlmQueries'
import { MilestoneCamera } from '@/types/vlm'
import { MilestoneCameraList, MilestoneCameraRow } from './MilestoneCameraList'

type MilestoneCameraSelectionProps = {
  serverId: string
  deviceId: string
  value: MilestoneCamera[]
  onChange: (cameras: MilestoneCamera[]) => void
  error?: ReactNode
  description?: ReactNode
  emptySelectionLabel?: ReactNode
}

const matchesSearch = (row: MilestoneCameraRow, search: string) => {
  const needle = search.trim().toLowerCase()
  return (
    !needle ||
    row.name.toLowerCase().includes(needle) ||
    row.camera_id.toLowerCase().includes(needle)
  )
}

export const MilestoneCameraSelection = ({
  serverId,
  deviceId,
  value,
  onChange,
  error: selectionError,
  description,
  emptySelectionLabel
}: MilestoneCameraSelectionProps) => {
  const intl = useIntl()
  const [search, setSearch] = useState('')

  const { data, isLoading, isFetching, isError, error, refetch } =
    useListMilestoneServerCameras({ serverId, deviceId })

  const discovered = data?.cameras ?? []
  const discoveredIds = new Set(discovered.map((c) => c.camera_id))
  const selectedIds = new Set(value.map((c) => c.camera_id))

  // Keep saved cameras the server no longer returns, so a camera that dropped
  // out is removed deliberately rather than silently.
  const missing = data
    ? value.filter((c) => !discoveredIds.has(c.camera_id))
    : []

  const rows: MilestoneCameraRow[] = [
    ...discovered.map((c) => ({ ...c, missing: false })),
    ...missing.map((c) => ({ ...c, enabled: true, missing: true }))
  ]
  const visibleRows = rows.filter((row) => matchesSearch(row, search))

  // Store the selection in discovery order, not click order, so saved
  // pipelines diff cleanly.
  const setSelection = (ids: Set<string>) => {
    const selection: MilestoneCamera[] = rows
      .filter((row) => ids.has(row.camera_id))
      .map(({ camera_id, name, mac_address }) => ({
        camera_id,
        name,
        mac_address
      }))
    onChange(selection)
  }

  const handleToggle = (row: MilestoneCameraRow) => {
    const ids = new Set(selectedIds)
    if (ids.has(row.camera_id)) ids.delete(row.camera_id)
    else ids.add(row.camera_id)
    setSelection(ids)
  }

  const handleToggleAll = (targets: MilestoneCameraRow[], select: boolean) => {
    const ids = new Set(selectedIds)
    targets.forEach((row) =>
      select ? ids.add(row.camera_id) : ids.delete(row.camera_id)
    )
    setSelection(ids)
  }

  const renderCameras = () => {
    if (isLoading)
      return (
        <Stack align="center" gap="xs" py="xl">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            <FormattedMessage id="vlm.pipelines.create.cameras.loading" />
          </Text>
        </Stack>
      )

    if (isError)
      return (
        <PlatformAlert variant="warning">
          <Stack gap="xs" align="flex-start">
            <Text size="sm">
              {extractErrorMessage(
                error,
                intl.formatMessage({
                  id: 'vlm.pipelines.create.cameras.error'
                }),
                { includeFieldNameInValidationErrors: false }
              )}
            </Text>
            <Button
              size="xs"
              variant="light"
              leftSection={<IconRefresh size={14} />}
              onClick={() => void refetch()}
            >
              <FormattedMessage id="vlm.pipelines.create.cameras.retry" />
            </Button>
          </Stack>
        </PlatformAlert>
      )

    return (
      <MilestoneCameraList
        rows={visibleRows}
        selectedIds={selectedIds}
        onToggle={handleToggle}
        onToggleAll={handleToggleAll}
      />
    )
  }

  return (
    <Stack gap="md">
      {description}

      <Input.Wrapper
        label={<FormattedMessage id="vlm.pipelines.create.cameras.label" />}
        description={
          value.length === 0 && emptySelectionLabel ? (
            emptySelectionLabel
          ) : (
            <FormattedMessage
              id="vlm.pipelines.create.cameras.selected"
              values={{ count: value.length }}
            />
          )
        }
        error={selectionError}
      >
        <Paper my={4} p="sm" withBorder>
          <Stack gap="sm">
            <Group gap="sm" wrap="nowrap">
              <TextInput
                style={{ flex: 1 }}
                leftSection={<IconSearch size={16} />}
                placeholder={intl.formatMessage({
                  id: 'vlm.pipelines.create.cameras.search'
                })}
                value={search}
                onChange={(event) => setSearch(event.currentTarget.value)}
              />
              <Button
                variant="light"
                leftSection={<IconRefresh size={16} />}
                loading={isFetching}
                onClick={() => void refetch()}
              >
                <FormattedMessage id="vlm.pipelines.create.cameras.reload" />
              </Button>
            </Group>

            {renderCameras()}
          </Stack>
        </Paper>
      </Input.Wrapper>
    </Stack>
  )
}
